import React, { memo, useState, useEffect, useCallback } from 'react';
import type {
  TapTarget,
  LocalizedLabel,
  PaywallHeaderStyle,
  StepNode,
} from '@segmently/schema-models/v2';
import type { OnActionCallback } from '../../../types/events';
import { createButtonClickEvent } from '../../../utils/eventEmitter';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import { resolveScreenForegroundColor } from '../../../utils/canvasForeground';
import { Text } from '../Text';
import { Button } from '../Button';
import { useElementAnimation, mergeAnimationClassName } from '../../../animations';
import { paywallHeaderRenders } from '../../../utils/renderPredicates';
import styles from './PaywallHeader.module.css';

const CloseIcon: React.FC<{ color?: string }> = ({ color = 'currentColor' }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill={color}>
    <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

function normalizeCloseLabel(closeButton: TapTarget, locale: string): TapTarget {
  if (closeButton.kind !== 'label') {
    return closeButton;
  }

  const content = closeButton.content as LocalizedLabel | undefined;
  const translations = content?.translations ?? {};
  const localeValue = translations[locale];
  const fallbackValue = Object.values(translations).find((value): value is string => typeof value === 'string');
  const hasVisibleLabel = Boolean((typeof localeValue === 'string' ? localeValue : fallbackValue)?.trim());

  if (hasVisibleLabel) {
    return closeButton;
  }

  return {
    ...closeButton,
    content: {
      kind: 'static',
      ...content,
      appearance: content?.appearance ?? {},
      translations: {
        ...translations,
        [locale]: 'Close',
      },
    },
  };
}

export interface PaywallHeaderProps {
  closeButton?: TapTarget;
  restoreLabel?: LocalizedLabel;
  headerAppearance?: PaywallHeaderStyle;
  step: StepNode;
  locale: string;
  onAction?: OnActionCallback;
  onRestoreClick?: () => void;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  absolute?: boolean;
}

export const PaywallHeader = memo(function PaywallHeader({
  closeButton,
  restoreLabel,
  headerAppearance,
  step,
  locale,
  onAction,
  onRestoreClick,
  className,
  'data-testid': testId,
  editPath,
  absolute = false,
}: PaywallHeaderProps): React.ReactElement | null {
  // V2: HorizontalEdge uses 'start'/'end' instead of 'left'/'right'
  // In LTR: start→left, end→right
  const closeAlignment = headerAppearance?.closeAlignment || 'start';
  const closePosition = closeAlignment === 'end' ? 'right' : 'left';
  const restorePosition = closePosition === 'right' ? 'left' : 'right';

  // V2: 'delayedVisible' instead of v1's 'visibleAfterTimer'
  const closeVisibility = headerAppearance?.closeVisibility || 'visible';
  const closeDelaySeconds = headerAppearance?.closeDelaySeconds ?? 5;

  const [isCloseVisible, setIsCloseVisible] = useState<boolean>(
    closeVisibility !== 'delayedVisible'
  );

  useEffect(() => {
    if (closeVisibility !== 'delayedVisible') {
      setIsCloseVisible(true);
      return;
    }

    setIsCloseVisible(false);
    const timer = setTimeout(() => {
      setIsCloseVisible(true);
    }, closeDelaySeconds * 1000);

    return () => clearTimeout(timer);
  }, [closeVisibility, closeDelaySeconds]);

  const handleRestoreClick = useCallback(() => {
    onRestoreClick?.();
  }, [onRestoreClick]);

  const handleCloseClick = useCallback(() => {
    if (onAction && closeButton?.action) {
      onAction(createButtonClickEvent(closeButton.action.kind, step, editPath ? `${editPath}.closeButton` : undefined));
    }
  }, [onAction, closeButton, step, editPath]);

  // Screen entrance slot `paywall.header` (F14 / AMV-AC-038). The Paywall screen passes editPath
  // `content.paywall`; FlexibleLayout sections pass a section path, never in the plan.
  const headerAnim = useElementAnimation(editPath ? `${editPath}.header` : undefined);

  // Renders nothing without a close button or a restore label (shared with the entrance plan)
  if (!paywallHeaderRenders(closeButton, restoreLabel)) {
    return null;
  }

  const schemaTestId = testId ?? getSchemaTestId(editPath);

  const headerClassName = [
    styles.paywallHeader,
    absolute && styles.absolute,
    className,
  ].filter(Boolean).join(' ');

  const renderRestore = () => {
    if (!restoreLabel) return null;
    return (
      <div
        className={styles.restoreLink}
        onClick={handleRestoreClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleRestoreClick()}
        data-testid={schemaTestId ? `${schemaTestId}-restore` : undefined}
        data-edit-path={editPath ? `${editPath}.restoreLabel` : undefined}
      >
        <Text label={restoreLabel} locale={locale} />
      </div>
    );
  };

  const renderClose = () => {
    if (!closeButton || !isCloseVisible) return null;

    if (closeButton.kind === 'label') {
      return (
        <Button
          tapTarget={normalizeCloseLabel(closeButton, locale)}
          locale={locale}
          step={step}
          onAction={onAction}
          className={styles.closeLabelButton}
          data-testid={schemaTestId ? `${schemaTestId}-close` : undefined}
          editPath={editPath ? `${editPath}.closeButton` : undefined}
        />
      );
    }

    // WP8 UX M-4: with no authored `iconColor` the glyph fills with `currentColor`, which
    // on a dark canvas is the inherited engine default black — an invisible escape from a
    // purchase surface. Fall back to the canvas's own readable foreground.
    const iconColor =
      (closeButton.appearance as { iconColor?: string } | undefined)?.iconColor ??
      resolveScreenForegroundColor(step as Parameters<typeof resolveScreenForegroundColor>[0]);

    return (
      <button
        type="button"
        className={styles.closeButton}
        onClick={handleCloseClick}
        data-testid={schemaTestId ? `${schemaTestId}-close` : undefined}
        data-edit-path={editPath ? `${editPath}.closeButton` : undefined}
        aria-label="Close"
      >
        <CloseIcon color={iconColor} />
      </button>
    );
  };

  return (
    <nav
      className={mergeAnimationClassName(headerClassName, headerAnim)}
      style={headerAnim.style}
      data-anim={headerAnim['data-anim']}
      data-testid={schemaTestId}
      data-edit-path={editPath}
    >
      <div className={styles.left}>
        {restorePosition === 'left' ? renderRestore() : null}
        {closePosition === 'left' ? renderClose() : null}
      </div>

      <div className={styles.right}>
        {restorePosition === 'right' ? renderRestore() : null}
        {closePosition === 'right' ? renderClose() : null}
      </div>
    </nav>
  );
});

PaywallHeader.displayName = 'PaywallHeader';
